const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/pages/ReportPage/ReportListRow.tsx';
import { useTheme } from '@mui/material/styles';
import { css, cx } from '@emotion/css';
import { Tooltip, useMediaQuery } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { Report } from '@cistec/common_web/defs/kisim';
import { KISIM_STD_DATE_FORMAT, KISIM_TIME_FORMAT } from '@cistec/common_web/defs/generalDefs';
import moment from 'moment/moment';
import textMap from 'App/pages/ReportPage/translations';
import { useIntl } from 'react-intl';
import Icon from '@mui/material/Icon';
import { Tag } from 'App/components/Tag';
import { IconClass, iconSize } from '@cistec/common_web/defs/icons';
import { makeSelectSignedReportInfo } from 'App/store/documents';
import { ReportPDFDialog } from './ReportPDFDialog';
import { useReportInfo } from './hooks';
import { selectNothing, useMadeSelector } from '@cistec/common_web/utils/store';

interface Props {
    report: Report;
}

function useCSSClasses() {
    const theme = useTheme();
    return useMemo(
        () => ({
            reportListRowCSS: css`
                cursor: pointer;
                padding: 0 ${theme.spacing(1)};

                .title {
                    flex: auto;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                    overflow: hidden;
                }

                .tags {
                    display: flex;
                    justify-content: flex-end;
                    white-space: nowrap;

                    > *:not(:first-child) {
                        margin-left: ${theme.spacing(1)};
                    }
                }

                ${theme.breakpoints.up('md')} {
                    & {
                        height: ${theme.spacing(4)};
                        display: flex;
                        align-items: center;

                        > *:not(:first-child) {
                            margin-left: ${theme.spacing(2)};
                        }

                        .date,
                        .tags {
                            flex: none;
                        }
                        .title {
                            flex: auto;
                        }
                    }
                }

                ${theme.breakpoints.down('md')} {
                    & {
                        > div {
                            height: ${theme.spacing(4)};
                            display: flex;
                            align-items: center;

                            > *:not(:first-child) {
                                margin-left: ${theme.spacing(2)};
                            }

                            :first-child {
                                .date {
                                    flex: none;
                                }
                                .tags {
                                    flex: auto;
                                }
                            }

                            :last-child {
                                .title {
                                    flex: auto;
                                }
                            }
                        }
                    }
                }
            `,
        }),
        [theme]
    );
}

export const ReportListRow = (props: Props) => {
    const theme = useTheme();

    const breakpointMedium = useMediaQuery(theme.breakpoints.down('md'));
    const classes = useCSSClasses();
    const { report } = props;
    const { formatMessage } = useIntl();

    const [showDocumentDialog, setShowDocumentDialog] = useState(false);

    const handleClick = () => {
        setShowDocumentDialog(true);
    };

    const handleDialogClose = () => {
        setShowDocumentDialog(false);
    };

    const { hasSignature, isDocumentRead, needsSignature, needsToBeRead, imageInfo } = useReportInfo(report);
    const { sender, code } = imageInfo;

    const signReportSuccessInfo = useMadeSelector(
        () => (sender && code ? makeSelectSignedReportInfo(sender, code) : selectNothing),
        [sender, code]
    );

    useEffect(() => {
        if (signReportSuccessInfo) {
            setShowDocumentDialog(false);
        }
    }, [signReportSuccessInfo]);

    const date = moment(report.DAT, KISIM_TIME_FORMAT).format(KISIM_STD_DATE_FORMAT);
    const text = report.NAME;

    const dateElement = <div className="date">{date}</div>;
    const titleElement = <div className="title">{text}</div>;

    const readConfirmationRequiredTag = needsToBeRead ? (
        <Tooltip title={formatMessage(textMap.readConfirmationRequiredTooltip)}>
            <Tag backgroundColor="#e7f2fa" color="#003a70">
                <Icon className={cx(IconClass.EyeOutlined, iconSize(16))} component="span" />
                <span>{formatMessage(textMap.readConfirmationRequired)}</span>
            </Tag>
        </Tooltip>
    ) : null;

    const documentReadTag = isDocumentRead ? (
        <Tag color="#2F9E44">
            <Icon className={cx(IconClass.Checkmark, iconSize(16))} component="span" />
            <span>{formatMessage(textMap.read)}</span>
        </Tag>
    ) : null;

    const signatureRequiredTag = needsSignature ? (
        <Tooltip title={formatMessage(textMap.signatureRequiredTooltip)}>
            <Tag backgroundColor="#e7f2fa" color="#003a70">
                <Icon className={cx(IconClass.Pencil, iconSize(16))} component="span" />
                <span>{formatMessage(textMap.signatureRequired)}</span>
            </Tag>
        </Tooltip>
    ) : null;

    const documentSignedTag = hasSignature ? (
        <Tag color="#2F9E44">
            <Icon className={cx(IconClass.Checkmark, iconSize(16))} component="span" />
            <span>{formatMessage(textMap.signed)}</span>
        </Tag>
    ) : null;

    const tags = (
        <div className="tags">
            {readConfirmationRequiredTag}
            {documentReadTag}
            {signatureRequiredTag}
            {documentSignedTag}
        </div>
    );

    const getSingleLineRow = () => (
        <>
            {dateElement}
            {titleElement}
            {tags}
        </>
    );

    const getDoubleLineRows = () => (
        <>
            <div>
                {dateElement}
                {tags}
            </div>
            <div>{titleElement}</div>
        </>
    );

    return (
        <>
            {showDocumentDialog ? <ReportPDFDialog report={report} onClose={handleDialogClose} /> : null}
            <div className={classes.reportListRowCSS} onClick={handleClick}>
                {breakpointMedium ? getDoubleLineRows() : getSingleLineRow()}
            </div>
        </>
    );
};
